/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react'
import { Flex, Spinner } from '@radix-ui/themes'
import { Link } from '@tanstack/react-router'
import { IconPlus } from '@tabler/icons-react'
import { useGetChapter } from '@/services/chapterService'
import { useGetModule } from '@/services/modulesService'
import { ModuleItemType } from '@/types/module'
import { PagingInfo } from '@/types/user'
import { useModulesStore } from '@/stores/useModulesStore'
import { cn } from '@/lib/utils'
import { useDebounce } from '@/hooks/useDebounce'
import SearchBox from '@/components/ui/SearchBox'
import BaseContainer from '@/components/ui/base-container'
import { Button } from '@/components/ui/button'
import PageTitle from '@/components/PageTitle'
import { Main } from '@/components/layout/main'
import ImportQuestionModal from './components/ImportQuestionModal'
import QuestionListTable from './components/QuestionListTable'

const QuestionLib = () => {
  const { setSelectedModule } = useModulesStore()
  const { modules } = useGetModule()

  const [isOpenImportModal, setIsOpenImportModal] = useState(false)
  const [tab, setTab] = useState<string | undefined>(undefined)
  const [query, setQuery] = useState('')
  const [page, setPage] = useState(1)

  const debouncedQuery = useDebounce(query)

  useEffect(() => {
    setPage(1)
  }, [debouncedQuery])

  const { chapters, isLoadingChapter, meta, refetchChapter } = useGetChapter({
    module_id: tab ?? '',
    page,
    limit: 10,
    search: debouncedQuery,
  })

  useEffect(() => {
    if (modules && modules.length > 0) {
      setSelectedModule(modules[0])
      setTab(String(modules[0].id))
    }
  }, [modules])

  return (
    <Main>
      <PageTitle title='Question Library' />
      <div className=''>
        <div className='mb-4 py-1'>
          <ul className='mb-4 flex w-full gap-1 overflow-auto pb-1'>
            {modules?.map((item: ModuleItemType) => {
              const id = String(item?.id ?? '')
              const isActive = tab === id
              return (
                <li key={id}>
                  <button
                    onClick={() => {
                      setTab(item?.id?.toString())
                      setSelectedModule(item)
                      setPage(1)
                    }}
                    className={cn(
                      'group flex w-full items-center gap-3 rounded-[8px] border-[1px] border-[#EAECF0] bg-[#fff] px-4 py-2.5 text-sm capitalize transition-colors',
                      'focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0057B8]/30',
                      isActive
                        ? 'rounded-[8px] border-[1px] border-[#0069E0] bg-[#EBF4FF] text-[#0057B8]'
                        : 'text-[#98A2B3] hover:bg-[#F2F4F7] hover:text-[#003A80]'
                    )}
                    aria-label={item.name}
                  >
                    <span className='truncate text-base'>{item.name}</span>
                  </button>
                </li>
              )
            })}
          </ul>
        </div>

        <BaseContainer
          action={
            <div className='flex w-full items-center justify-between gap-2.5'>
              <Flex justify={'center'} align={'center'} gap={'3'}>
                <p className='text-2xl text-[#002652]'>Chapter List</p>
                <Link
                  to='/question-lib/$quesiontionLibId'
                  params={{ quesiontionLibId: '0' }}
                  search={{ source: 'questions' }}
                  className='text-sm text-[#0057B8] hover:underline'
                >
                  View All Questions
                </Link>
              </Flex>
              <Flex gap={'26px'}>
                <SearchBox
                  placeHolder='Search by Chapter Name'
                  onValueChange={(value: string) => setQuery(value)}
                />
                <Button
                  variant='default'
                  className='px-3.5 py-2.5'
                  onClick={() => setIsOpenImportModal(true)}
                >
                  <IconPlus />
                  Import
                </Button>
              </Flex>
            </div>
          }
        >
          {isLoadingChapter ? (
            <div className='flex w-full justify-center py-10'>
              <Spinner size={'3'} />
            </div>
          ) : (
            <QuestionListTable
              meta={meta as PagingInfo}
              data={chapters ?? []}
              page={page}
              handlePageChange={setPage}
              refetchChapter={refetchChapter}
            />
          )}
        </BaseContainer>
      </div>
      {isOpenImportModal && (
        <ImportQuestionModal
          refreshChapter={() => refetchChapter()}
          moduleId={tab ?? ''}
          open={isOpenImportModal}
          onOpenChange={() => setIsOpenImportModal(!isOpenImportModal)}
        />
      )}
    </Main>
  )
}

export default QuestionLib
